import {
  Component,
  For,
  Show,
  createSignal,
  createMemo,
  createEffect,
  JSX,
} from "solid-js";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  class?: string;
  onChange?: (value: string) => void;
  renderOption?: (option: SelectOption) => JSX.Element;
  renderTrigger?: (option: SelectOption | undefined) => JSX.Element;
}

export const Select: Component<SelectProps> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [search, setSearch] = createSignal("");
  const [highlightedIndex, setHighlightedIndex] = createSignal(0);

  let rootRef: HTMLDivElement | undefined;
  let triggerRef: HTMLButtonElement | undefined;
  let searchRef: HTMLInputElement | undefined;

  const filteredOptions = createMemo(() => {
    if (!props.searchable || !search()) return props.options;
    const query = search().toLowerCase();
    return props.options.filter((opt) =>
      opt.label.toLowerCase().includes(query),
    );
  });

  const selectedOption = createMemo(() =>
    props.options.find((opt) => opt.value === props.value),
  );

  const displayValue = () =>
    selectedOption()?.label ?? props.placeholder ?? "Select...";

  createEffect(() => {
    if (!isOpen() || !props.searchable) return;
    requestAnimationFrame(() => {
      // A fast open/close leaves searchRef on a node <Show> already unmounted.
      if (isOpen()) searchRef?.focus();
    });
  });

  const closeDropdown = () => {
    const returnFocus = document.activeElement === searchRef;
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(0);
    // <Show> unmounts the focused input, which would drop focus on <body>.
    if (returnFocus) triggerRef?.focus();
  };

  const selectOption = (option: SelectOption) => {
    props.onChange?.(option.value);
    closeDropdown();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.disabled) return;
    const options = filteredOptions();

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen()) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((i) => Math.min(i + 1, options.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (isOpen() && options[highlightedIndex()]) {
          selectOption(options[highlightedIndex()]);
        } else {
          setIsOpen(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown();
        break;
    }
  };

  const handleBlur = (e: FocusEvent) => {
    const target = e.relatedTarget as HTMLElement | null;
    // Scoped to *this* root, not the panel: the trigger is a sibling of the
    // panel, so a panel-scoped guard closed on the trigger's own mousedown and
    // let the ensuing click toggle it back open. Identity, not a bare selector
    // match — every instance carries the attribute, and matching a *sibling*
    // Select's root would leave two dropdowns open at once.
    if (target?.closest("[data-select-root]") !== rootRef) {
      setIsOpen(false);
      setSearch("");
    }
  };

  return (
    <div
      ref={rootRef}
      data-select-root
      class={"relative " + (props.class ?? "")}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        class={
          "w-full flex items-center justify-between rounded border h-7 px-3 py-0 text-sm bg-surface border-line text-left transition-colors focus-ring " +
          (props.disabled
            ? "opacity-50 cursor-not-allowed bg-sunken"
            : "cursor-pointer")
        }
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen()}
        onClick={() => {
          if (props.disabled) return;
          if (isOpen()) closeDropdown();
          else setIsOpen(true);
        }}
        onBlur={handleBlur}
      >
        <Show
          when={props.renderTrigger}
          fallback={
            <span class={!selectedOption() ? "text-ink-3" : "text-ink"}>
              {displayValue()}
            </span>
          }
        >
          {props.renderTrigger?.(selectedOption())}
        </Show>
        <svg
          class={
            "w-4 h-4 text-ink-3 transition-transform " +
            (isOpen() ? "rotate-180" : "")
          }
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <Show when={isOpen()}>
        <div class="absolute z-10 w-full mt-1 bg-surface border border-line rounded shadow-overlay">
          <Show when={props.searchable}>
            <div class="p-2 border-b border-line">
              <input
                ref={searchRef}
                type="text"
                class="w-full px-2 py-1.5 text-sm border border-line rounded bg-surface focus-ring"
                placeholder="Search..."
                value={search()}
                onInput={(e) => {
                  setSearch(e.currentTarget.value);
                  setHighlightedIndex(0);
                }}
                onBlur={handleBlur}
              />
            </div>
          </Show>
          <ul class="max-h-60 overflow-auto py-1" role="listbox">
            <For
              each={filteredOptions()}
              fallback={
                <li class="px-3 py-2 text-sm text-ink-3">No options</li>
              }
            >
              {(option, index) => (
                <li
                  role="option"
                  aria-selected={option.value === props.value}
                  class={
                    "px-3 py-2 text-sm cursor-pointer " +
                    (option.value === props.value
                      ? "bg-signal-cyan-tint text-ink font-medium"
                      : "text-ink-2") +
                    " " +
                    (index() === highlightedIndex()
                      ? "bg-sunken"
                      : "hover:bg-paper")
                  }
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  {props.renderOption
                    ? props.renderOption(option)
                    : option.label}
                </li>
              )}
            </For>
          </ul>
        </div>
      </Show>
    </div>
  );
};
